// Cálculo puro dos cards do painel de habilitação (PO1-3106) a partir do
// GET /habilitacao/painel. Sem React: testável com CI=true npm test.
//
// Cards: Cadastro (dados principais + contato de TI por embarcador),
// Integração CT-e (informativo desde a PO1-3281: o canal é montado no
// onboarding, depois da homologação, e não bloqueia mais) e Homologação
// (por embarcador; a aprovação é da Everlog).
//
// Fases por vínculo (PO1-3281, épico PO1-3279): o backend devolve `fase`
// calculada do Status da habilitação — EM_CONFIGURACAO (critérios pendentes),
// EM_HOMOLOGACAO (critérios ok, falta a Everlog liberar), DEVOLVIDO,
// EM_ONBOARDING (homologada; pastas/SFTP/portal em andamento), OPERACIONAL e
// REMOVIDO. `AGUARDANDO_HOMOLOGACAO` é o mesmo que EM_CONFIGURACAO na carteira
// do embarcador; fica no mapa para a tela aceitar as duas grafias.

export const MACRO_LABEL = {
  EM_CONFIGURACAO: "Em configuração",
  EM_HOMOLOGACAO: "Em homologação",
  EM_ONBOARDING: "Em onboarding",
  OPERACIONAL: "Operacional",
};

export const FASE_LABEL = {
  EM_CONFIGURACAO: "Aguardando homologação",
  AGUARDANDO_HOMOLOGACAO: "Aguardando homologação",
  EM_HOMOLOGACAO: "Em homologação",
  DEVOLVIDO: "Devolvido",
  EM_ONBOARDING: "Em onboarding",
  OPERACIONAL: "Operacional",
  REMOVIDO: "Removido",
};

const FALTANDO_LABEL = {
  cnpj: "CNPJ",
  razaoSocial: "razão social",
  cep: "CEP",
  logradouro: "logradouro",
};

export function descreverFaltando(faltando) {
  return (faltando || []).map((f) => FALTANDO_LABEL[f] || f).join(", ");
}

function badgeDe(done, emAndamento) {
  if (done) return { badge: "Concluído", badgeTipo: "done" };
  if (emAndamento) return { badge: "Em andamento", badgeTipo: "auto" };
  return { badge: "Pendente", badgeTipo: "pend" };
}

const FASES_HOMOLOGADAS = ["OPERACIONAL", "EM_ONBOARDING"];

export function estadoPainel(painel) {
  const vinculos = (painel && painel.vinculos) || [];
  const macro = (painel && painel.macro) || "EM_CONFIGURACAO";
  const criteriosDe = (v) => (v && v.criterios) || {};

  // Dados principais são da transportadora (iguais em todos os vínculos):
  // basta um vínculo para ler; sem vínculo, considerar pendente.
  const primeiro = vinculos[0];
  const dados = primeiro ? criteriosDe(primeiro).dadosPrincipais || { ok: false, faltando: [] } : { ok: false, faltando: [] };
  const contatosTi = vinculos.map((v) => ({
    idVinculo: v.id,
    embarcador: v.embarcador,
    ok: !!(criteriosDe(v).contatoTi && criteriosDe(v).contatoTi.ok),
    contato: (criteriosDe(v).contatoTi && criteriosDe(v).contatoTi.contato) || null,
  }));
  const cadastroDone = vinculos.length > 0 && dados.ok && contatosTi.every((c) => c.ok);
  const cadastroAndamento = dados.ok || contatosTi.some((c) => c.ok);

  const integracoes = vinculos.map((v) => ({
    idVinculo: v.id,
    embarcador: v.embarcador,
    ok: !!(criteriosDe(v).canalCte && criteriosDe(v).canalCte.ok),
    origem: (criteriosDe(v).canalCte && criteriosDe(v).canalCte.origem) || null,
    primeiroCte: !!(criteriosDe(v).primeiroCte && criteriosDe(v).primeiroCte.ok),
    emOnboarding: v.fase === "EM_ONBOARDING",
  }));
  const integracaoDone = vinculos.length > 0 && integracoes.every((i) => i.ok);
  const integracaoAndamento = integracoes.some((i) => i.ok || i.emOnboarding);

  const homologacoes = vinculos.map((v) => ({
    idVinculo: v.id,
    embarcador: v.embarcador,
    status: v.status,
    fase: v.fase,
    faseLabel: FASE_LABEL[v.fase] || v.fase,
    devolvido: v.status === "DEVOLVIDO",
    motivoDevolucao: v.motivoDevolucao || null,
    homologado: v.status === "HOMOLOGADO" || FASES_HOMOLOGADAS.indexOf(v.fase) >= 0,
    emOnboarding: v.fase === "EM_ONBOARDING",
    prontoParaHomologar: v.fase === "EM_HOMOLOGACAO",
  }));
  const homologacaoDone = macro === "OPERACIONAL" || macro === "EM_ONBOARDING";
  const homologacaoAndamento = homologacoes.some((h) => h.prontoParaHomologar || h.homologado);

  const cards = [
    {
      key: "cadastro",
      titulo: "Cadastro",
      // O que falta aparece na própria linha de "Dados da empresa", junto do botão
      // que resolve (20/09/2026). Aqui fica só o que a etapa contém.
      sub: "Dados da empresa e um contato de TI por embarcador",
      done: cadastroDone,
      ...badgeDe(cadastroDone, cadastroAndamento),
    },
    {
      key: "integracao",
      titulo: "Integração CT-e",
      // PO1-3281: informativo. O canal (pasta SFTP e usuário) é montado no
      // onboarding de cada embarcador, depois da homologação; não bloqueia.
      sub: "Canal de CT-e por embarcador (pasta SFTP ou conector), montado no onboarding",
      done: integracaoDone,
      ...badgeDe(integracaoDone, integracaoAndamento),
    },
    {
      key: "homologacao",
      titulo: "Homologação",
      sub: "Aprovação da Everlog, por transportadora; na primeira, o onboarding de cada embarcador começa",
      done: homologacaoDone,
      // Só o cadastro trava a homologação: o canal de CT-e saiu dos critérios (PO1-3281).
      locked: !cadastroDone,
      ...(homologacaoDone
        ? { badge: "Concluído", badgeTipo: "done" }
        : homologacaoAndamento
        ? { badge: "Em homologação", badgeTipo: "auto" }
        : !cadastroDone
        ? { badge: "Bloqueado", badgeTipo: "lock" }
        : { badge: "Pendente", badgeTipo: "pend" }),
    },
  ];

  return {
    macro,
    macroLabel: MACRO_LABEL[macro] || macro,
    cards,
    concluidos: cards.filter((c) => c.done).length,
    total: cards.length,
    dados,
    contatosTi,
    integracoes,
    homologacoes,
    semVinculo: vinculos.length === 0,
  };
}
